 /**
 * A Svelte action to dispatch event on click outside of the DOM node.
 * 
 * If this node is displayed based on an event emitted from an 'outside' node (e.g. nav link triggering a dropdown)
 * you can pass this 'triggerElement' on the params object. Grab a reference to the node using `bind:this={myTriggerDOMVariable}`
 * or document queries, then `<ul use:clickOutside={{triggerElement}} on:CLICK_OUTSIDE={myClickOutsideHandlerFunction}>`
 * 
 * In scenarios where the dynamic element you'd be using this on covers the whole UI (e.g. a modal/overlay),
 * any click outside the content will be on the 'backdrop', and it's not possible to click the triggering element.
 * This obviates the need to ensure the outside click didn't land on the UI that triggers the element's existence.
 * 
 * At the end of the day, this will only be emitting an event. It's up to you do listen for the event and 
 * do something (probably destroy the component) when it fires.
 * 
 * @param {HTMLElement} node 
 * @param {Object|undefined} params 
 * @returns {Object}
 * 
 * @see https://svelte.dev/docs/element-directives#use-action
 * @see https://svelte.dev/repl/0ace7a508bd843b798ae599940a91783?version=3.16.7
 */
export function clickOutside(node, params = undefined) {
  const checkForTriggerUI = (event)=> {
    if(params && mobileTriggerElement?.contains(event.target) || desktopTriggerElement?.contains(event.target)) return true
    return false
  }

  const handleClick = (event)=> {
    if (node && !node.contains(event.target) && !checkForTriggerUI(event) && !event.defaultPrevented) {
      node.dispatchEvent(
        new CustomEvent('CLICK_OUTSIDE', node)
      )
    }
  }

  const handleEscape = (event)=> {
    if (node && !event.defaultPrevented && event.code === "Escape") {
      node.dispatchEvent(
        new CustomEvent('CLICK_OUTSIDE', node)
      )
    }
  }

	document.addEventListener('click', handleClick, true)
	document.addEventListener('keyup', handleEscape, true)
  
  const mobileTriggerElement = params && params.hasOwnProperty('mobileTriggerElement') ? params.mobileTriggerElement : undefined
  const desktopTriggerElement = params && params.hasOwnProperty('desktopTriggerElement') ? params.desktopTriggerElement : undefined

  return {    
    destroy() {
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('keyup', handleEscape, true)
    }
	}
}